import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { showError } from "@/lib/errors";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Inbox, Crown, Building2, Users, ExternalLink,
  Clock3, RefreshCw, ShieldAlert, Loader2, Newspaper, Image as ImageIcon, AlertTriangle,
} from "lucide-react";

const KIND_META = {
  athlete:    { label: "Athlète",     Icon: Users,     route: "/athletes" },
  coach:      { label: "Coach",       Icon: Users,     route: "/coachs" },
  club:       { label: "Club",        Icon: Building2, route: "/clubs" },
  federation: { label: "Fédération",  Icon: Crown,     route: "/federations" },
};

const STATUS_META = {
  declared:  { label: "Brouillon",  variant: "outline",     Icon: Clock3 },
  pending:   { label: "En attente", variant: "secondary",   Icon: Clock3 },
  accepted:  { label: "Validé",     variant: "default",     Icon: CheckCircle2 },
  rejected:  { label: "Refusé",     variant: "destructive", Icon: XCircle },
  revoked:   { label: "Révoqué",    variant: "outline",     Icon: XCircle },
};

const ROLE_LABELS = {
  athlete_of_club:         "souhaite être affilié·e au club",
  athlete_of_federation:   "souhaite être affilié·e à la fédération",
  athlete_coached_by:      "déclare être coaché·e par",
  coach_of_club:           "coach du club",
  club_of_federation:      "club affilié à la fédération",
};

function Initials({ name }) {
  const init = (name || "?").split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return <AvatarFallback>{init}</AvatarFallback>;
}

function fmtPeriod(s, e) {
  if (!s && !e) return null;
  if (s && e) return `${s} – ${e}`;
  if (s) return `Depuis ${s}`;
  return `Jusqu'en ${e}`;
}

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 30) return `il y a ${Math.floor(diff / 86400)} j`;
  return d.toLocaleDateString("fr-FR");
}

/* ------------------------------------------------------------------------- */
/* Carte d'une proposition Curator (article tagué sur une fiche possédée)   */
/* ------------------------------------------------------------------------- */
function CuratorProposalCard({ item, ownedIds, onResponded }) {
  const [busy, setBusy] = useState(null); // "accept" | "reject" | "correction"
  const [note, setNote] = useState("");
  const [expanded, setExpanded] = useState(false);

  // Filtrer les associations qui concernent l'utilisateur (entités possédées) et qui sont en pending
  const myAssocs = (item.associations || [])
    .map((a, idx) => ({ ...a, idx }))
    .filter((a) => (a.owner_response || "pending") === "pending")
    .filter((a) => (ownedIds[a.entity_type] || []).includes(a.entity_id));

  if (myAssocs.length === 0) return null;

  const respond = async (assocIdx, response) => {
    setBusy(`${response}-${assocIdx}`);
    try {
      await api.post(`/curator/items/${item.id}/associations/${assocIdx}/respond`, {
        response,
        note: note || null,
      });
      const verb = response === "accept" ? "ajouté" : response === "reject" ? "refusé" : "demande de correction envoyée";
      toast.success(`Article ${verb}.`);
      onResponded?.(item.id, assocIdx, response);
    } catch (e) {
      showError(e, { fallback: "Échec" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="overflow-hidden" data-testid={`curator-inbox-card-${item.id}`}>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="sm:w-48 aspect-video sm:aspect-square bg-muted shrink-0 relative">
          {item.image_url ? (
            <img src={item.image_url} alt="" className="object-cover w-full h-full" loading="lazy"
              onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <ImageIcon className="h-6 w-6 opacity-40" />
            </div>
          )}
          <Badge className="absolute top-2 left-2 bg-[hsl(var(--aito-lagoon))] text-black text-[10px]">
            <Newspaper className="h-2.5 w-2.5 mr-0.5" />Curator
          </Badge>
        </div>
        <div className="p-3 flex-1 min-w-0 space-y-2">
          <div>
            <h3 className="font-display font-semibold text-base leading-tight line-clamp-2" data-testid={`curator-inbox-title-${item.id}`}>
              {item.title}
            </h3>
            {item.summary && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{item.summary}</p>}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
            {item.source_name && <span className="font-medium">{item.source_name}</span>}
            {item.published_at && <span>· {relativeTime(item.published_at)}</span>}
            {item.url && (
              <a href={item.url} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5">
                <ExternalLink className="h-2.5 w-2.5" />Lire la source
              </a>
            )}
          </div>
          {/* Pour chaque entité possédée taguée — bloc actions */}
          {myAssocs.map((a) => (
            <div key={a.idx} className="border rounded-md p-2.5 space-y-2 bg-muted/30" data-testid={`curator-inbox-assoc-${item.id}-${a.idx}`}>
              <div className="flex items-center gap-2 text-xs">
                <Badge variant="outline" className="capitalize text-[10px]">{a.entity_type}</Badge>
                <span className="font-medium truncate">{a.entity_name}</span>
                <AlertTriangle className="h-3 w-3 text-amber-500 ml-auto" />
                <span className="text-amber-600 dark:text-amber-400 text-[10px]">Réponse attendue</span>
              </div>
              {expanded && (
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Note (visible pour l'admin / le contributeur)…"
                  rows={2}
                  className="text-xs"
                  data-testid={`curator-inbox-note-${item.id}-${a.idx}`}
                />
              )}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setExpanded((v) => !v)}
                >
                  {expanded ? "Masquer la note" : "Ajouter une note"}
                </Button>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => respond(a.idx, "correction")}
                    disabled={busy !== null}
                    data-testid={`curator-inbox-correction-${item.id}-${a.idx}`}
                  >
                    {busy === `correction-${a.idx}` ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                    Demander correction
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => respond(a.idx, "reject")}
                    disabled={busy !== null}
                    data-testid={`curator-inbox-reject-${item.id}-${a.idx}`}
                  >
                    {busy === `reject-${a.idx}` ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <XCircle className="h-3.5 w-3.5 mr-1" />}
                    Refuser
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => respond(a.idx, "accept")}
                    disabled={busy !== null}
                    data-testid={`curator-inbox-accept-${item.id}-${a.idx}`}
                  >
                    {busy === `accept-${a.idx}` ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                    Accepter
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------------- */
/* Carte d'une demande                                                        */
/* ------------------------------------------------------------------------- */
function RequestCard({ link, onAction }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(null); // "accept" | "reject" | null
  const [expanded, setExpanded] = useState(false);

  const src = link.source_entity;
  const tgt = link.target_entity;
  const srcMeta = KIND_META[link.source_type] || KIND_META.athlete;
  const tgtMeta = KIND_META[link.target_type] || KIND_META.club;
  const statusMeta = STATUS_META[link.status] || STATUS_META.pending;
  const StatusIcon = statusMeta.Icon;

  const performAction = async (action) => {
    setBusy(action);
    try {
      await api.patch(`/entity-links/${link.id}`, { action, note: note || null });
      toast.success(action === "accept" ? "Lien validé ✅" : "Demande refusée");
      onAction?.(link.id);
    } catch (e) {
      showError(e, { fallback: "Échec de l'opération" });
    } finally { setBusy(null); }
  };

  return (
    <Card className="p-4 space-y-3" data-testid={`inbox-card-${link.id}`}>
      {/* Header : source -> target */}
      <div className="flex items-start gap-3">
        <Link to={src?.slug ? `${srcMeta.route}/${src.slug}` : "#"} className="shrink-0">
          <Avatar className="h-12 w-12 ring-2 ring-[hsl(var(--aito-lagoon)/0.2)]">
            {src?.image_url && <AvatarImage src={src.image_url} alt={src.name} />}
            <Initials name={src?.name} />
          </Avatar>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Link
                  to={src?.slug ? `${srcMeta.route}/${src.slug}` : "#"}
                  className="font-display font-semibold text-sm hover:underline truncate"
                  data-testid={`inbox-source-${link.id}`}
                >
                  {src?.name || "Entité inconnue"}
                </Link>
                <Badge variant="outline" className="text-[10px]">{srcMeta.label}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {ROLE_LABELS[link.role] || "souhaite se lier à"}
                {" "}
                <Link
                  to={tgt?.slug ? `${tgtMeta.route}/${tgt.slug}` : "#"}
                  className="font-medium text-foreground hover:underline"
                >
                  {tgt?.name || link.target_name_snapshot}
                </Link>
              </p>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge variant={statusMeta.variant} className="text-[10px] gap-1">
                <StatusIcon className="h-3 w-3" />
                {statusMeta.label}
              </Badge>
              {link.via === "federation_oversight" && (
                <Badge variant="outline" className="text-[10px] gap-1 border-amber-400/40 text-amber-600 dark:text-amber-400">
                  <ShieldAlert className="h-3 w-3" />Tutelle fédérale
                </Badge>
              )}
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
            <span>Soumis {relativeTime(link.submitted_at || link.declared_at)}</span>
            {fmtPeriod(link.period_start, link.period_end) && (
              <>· <span>{fmtPeriod(link.period_start, link.period_end)}</span></>
            )}
            {link.evidence_url && (
              <>·{" "}
                <a href={link.evidence_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 underline">
                  Preuve <ExternalLink className="h-2.5 w-2.5" />
                </a>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      {link.status === "pending" && (
        <>
          {expanded && (
            <Textarea
              data-testid={`inbox-note-${link.id}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note interne (visible par le déclarant si refus)…"
              rows={2}
              className="text-sm"
            />
          )}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`inbox-toggle-note-${link.id}`}
            >
              {expanded ? "Masquer la note" : "Ajouter une note"}
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => performAction("reject")}
                disabled={busy !== null}
                data-testid={`inbox-reject-${link.id}`}
              >
                {busy === "reject" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <XCircle className="h-3.5 w-3.5 mr-1" />}
                Refuser
              </Button>
              <Button
                size="sm"
                onClick={() => performAction("accept")}
                disabled={busy !== null}
                data-testid={`inbox-accept-${link.id}`}
              >
                {busy === "accept" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                Valider
              </Button>
            </div>
          </div>
        </>
      )}

      {link.status !== "pending" && link.review_note && (
        <p className="text-xs text-muted-foreground italic border-l-2 border-border pl-2">
          Note : {link.review_note}
        </p>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------------- */
/* Page                                                                       */
/* ------------------------------------------------------------------------- */
export default function ValidationInboxPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Curator inbox
  const [curatorItems, setCuratorItems] = useState([]);
  const [curatorLoading, setCuratorLoading] = useState(true);
  const [ownedIds, setOwnedIds] = useState({ athlete: [], coach: [], club: [], federation: [] });

  const load = async (status = tab) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/entity-links/inbox?status=${status}`);
      setItems(data?.items || []);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error("Impossible de charger la boîte de validation.");
    } finally { setLoading(false); }
  };

  const loadCurator = async () => {
    setCuratorLoading(true);
    try {
      // Récupère les entités possédées (pour filtrage local)
      const [a, c, cl, f] = await Promise.all([
        api.get("/athletes/mine").catch(() => ({ data: [] })),
        api.get("/coaches/mine").catch(() => ({ data: [] })),
        api.get("/clubs/mine").catch(() => ({ data: [] })),
        api.get("/federations/mine").catch(() => ({ data: [] })),
      ]);
      const getIds = (resp) => {
        const items = Array.isArray(resp?.data?.items) ? resp.data.items : (Array.isArray(resp?.data) ? resp.data : []);
        return items.map((x) => x.id).filter(Boolean);
      };
      setOwnedIds({
        athlete: getIds(a),
        coach: getIds(c),
        club: getIds(cl),
        federation: getIds(f),
      });
      // Inbox curator (pending uniquement)
      const { data } = await api.get("/curator/inbox?status=pending");
      setCuratorItems(data?.items || []);
    } catch (e) {
      if (e?.response?.status !== 401) {
        // Pas critique
        // eslint-disable-next-line no-console
        console.warn("Curator inbox load failed", e);
      }
      setCuratorItems([]);
    } finally { setCuratorLoading(false); }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/connexion?next=/mes-validations"); return; }
    load(tab);
    if (tab === "pending") loadCurator();
    // eslint-disable-next-line
  }, [authLoading, user, tab]);

  const counts = useMemo(() => {
    const c = { pending: 0, accepted: 0, rejected: 0, revoked: 0 };
    items.forEach((l) => { if (c[l.status] != null) c[l.status]++; });
    return c;
  }, [items]);

  const onAction = (linkId) => {
    setItems((prev) => prev.filter((l) => l.id !== linkId));
  };

  const onCuratorResponded = (itemId, assocIdx) => {
    // Retirer l'item de la liste (l'association ciblée est traitée)
    setCuratorItems((prev) => prev.map((it) => {
      if (it.id !== itemId) return it;
      const next = { ...it, associations: (it.associations || []).map((a, i) => i === assocIdx ? { ...a, owner_response: "accepted" } : a) };
      return next;
    }).filter((it) => {
      // garder l'item si au moins une association possédée reste pending
      const allKinds = ["athlete", "coach", "club", "federation"];
      return (it.associations || []).some((a) => (a.owner_response || "pending") === "pending"
        && allKinds.some((k) => (ownedIds[k] || []).includes(a.entity_id) && a.entity_type === k));
    }));
  };

  const curatorPendingCount = useMemo(() => {
    const allKinds = ["athlete", "coach", "club", "federation"];
    let n = 0;
    curatorItems.forEach((it) => {
      (it.associations || []).forEach((a) => {
        if ((a.owner_response || "pending") !== "pending") return;
        if (allKinds.some((k) => (ownedIds[k] || []).includes(a.entity_id) && a.entity_type === k)) n++;
      });
    });
    return n;
  }, [curatorItems, ownedIds]);

  return (
    <div className="container max-w-4xl mx-auto py-6 px-4 space-y-5" data-testid="validation-inbox-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <Inbox className="h-6 w-6 text-[hsl(var(--aito-lagoon))]" />
            Validations
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Les demandes ci-dessous concernent les entités dont tu es propriétaire (club, fédération ou coach). Valide ou refuse pour officialiser les liens publiés sur le site.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load(tab)} data-testid="inbox-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />Actualiser
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="inbox-tabs">
          <TabsTrigger value="pending" data-testid="inbox-tab-pending">
            En attente
            {curatorPendingCount > 0 && tab === "pending" && (
              <Badge className="ml-1.5 bg-[hsl(var(--aito-lagoon))] text-black h-4 px-1.5 text-[10px]">+{curatorPendingCount} Curator</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="accepted" data-testid="inbox-tab-accepted">Validées</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="inbox-tab-rejected">Refusées</TabsTrigger>
          <TabsTrigger value="all" data-testid="inbox-tab-all">Tout</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4 space-y-6">
          {/* --- Section Curator (uniquement sur "En attente") --- */}
          {tab === "pending" && (
            <section className="space-y-3" data-testid="curator-inbox-section">
              <div className="flex items-center gap-2">
                <Newspaper className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
                <h2 className="font-display font-semibold text-sm uppercase tracking-wide">
                  Articles à valider <span className="text-muted-foreground font-normal">({curatorPendingCount})</span>
                </h2>
              </div>
              {curatorLoading ? (
                <Skeleton className="h-32 w-full" />
              ) : curatorPendingCount === 0 ? (
                <Card className="p-4 text-xs text-muted-foreground italic" data-testid="curator-inbox-empty">
                  Aucun article récent à valider pour tes fiches.
                </Card>
              ) : (
                curatorItems.map((it) => (
                  <CuratorProposalCard
                    key={it.id}
                    item={it}
                    ownedIds={ownedIds}
                    onResponded={onCuratorResponded}
                  />
                ))
              )}
            </section>
          )}

          {/* --- Section Entity Links (existant) --- */}
          <section className="space-y-3">
            {tab === "pending" && (
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
                <h2 className="font-display font-semibold text-sm uppercase tracking-wide">
                  Demandes d'affiliation
                </h2>
              </div>
            )}
            {loading ? (
              <>
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </>
            ) : items.length === 0 ? (
              <Card className="p-8 text-center" data-testid="inbox-empty">
                <Inbox className="h-12 w-12 mx-auto text-muted-foreground mb-3 opacity-50" />
                <p className="text-sm font-medium">Rien à valider ici.</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {tab === "pending"
                    ? "Aucune demande d'affiliation n'attend ta validation pour le moment."
                    : tab === "accepted"
                      ? "Aucun lien validé dans l'historique."
                      : tab === "rejected"
                        ? "Aucun refus dans l'historique."
                        : "Tu n'es propriétaire d'aucune entité recevant des demandes."}
                </p>
                {tab === "pending" && (
                  <p className="text-xs text-muted-foreground mt-3 italic">
                    Astuce : assure-toi d'être déclaré comme propriétaire (owner) du club ou de la fédération concerné·e.
                  </p>
                )}
              </Card>
            ) : (
              items.map((l) => <RequestCard key={l.id} link={l} onAction={onAction} />)
            )}
          </section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
